import { useEffect } from 'react';

import { Flex, FloraButton } from '@grupoboticario/flora-react';
import {
  InformationCircleIcon,
  TruckIcon,
  ExclamationMarkTriangleFillIcon,
} from '@grupoboticario/flora-react-icons';
import Cookies from 'js-cookie';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import slugify from 'slugify';

import StylesDrawers from 'components/Drawers/Drawers.styles';
import { ResponsiveFloraTypography } from 'components/ResponsiveFloraTypography';
import config from 'config';
import { getUnformattedZipcode } from 'helpers';
import { useTracking, useUpdateChangeCadastral } from 'hooks';
import { ACCESS_TOKEN_KEY_CADASTRAL_CHANGE } from 'services/auth';
import { useRegistrationControllerContext } from 'store';

import Styles from './AddressModalConfirmation.styles';

import type { AnalyticsType } from 'domains/analytics.d';
import type { ChangeCadastralResponse, RequestChangeCadastralType } from 'domains/change-cadastral';

export const AddressModalConfirmation = () => {
  const { steps, handleModalForm, handleError, handleCorrectData } =
    useRegistrationControllerContext();
  const { handleSubmit } = useForm<RequestChangeCadastralType>();
  const { sendDataToGTM } = useTracking();
  const navigate = useNavigate();
  const hasChangeToken = Cookies.get(ACCESS_TOKEN_KEY_CADASTRAL_CHANGE);
  const {
    data,
    isSuccess,
    isLoading,
    isError,
    error,
    mutateAsync: changeCadastral,
  } = useUpdateChangeCadastral();

  const {
    data: dataRegistration,
    detail,
    errorId,
    userMessage,
    success: isSuccessRegistration,
  } = (data as unknown as ChangeCadastralResponse) || {};

  const { sent: isSent } = dataRegistration || {};

  const onSubmit: SubmitHandler<RequestChangeCadastralType> = async () => {
    if (steps.userData?.zipcode) {
      const addressFormatted = {
        address: steps.userData.address,
        number: steps.userData?.hasNoNumber ? 'S/N' : steps.userData?.number,
        complement: steps.userData.complement || '',
        district: steps.userData.district,
        city: steps.userData.city,
        state: steps.userData.state,
        zipcode: getUnformattedZipcode(steps.userData.zipcode),
      };
      await changeCadastral({ address: addressFormatted });
    }
  };

  useEffect(() => {
    if (!hasChangeToken) {
      navigate('/perfil', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChangeToken]);

  useEffect(() => {
    if (isSuccess && !isLoading) {
      handleModalForm();
      handleEventCallback('sucesso');
      navigate('/perfil/editar/endereco/sucesso', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isLoading]);

  useEffect(() => {
    if (isError || (isSuccess && !isSuccessRegistration && (!isSent || dataRegistration))) {
      handleEventCallback('erro');

      const errorMessage =
        userMessage?.errorTitle ??
        error?.message ??
        dataRegistration?.message ??
        (error as any)?.data?.errorId;

      handleError({
        detail,
        errorId,
        userMessage,
        error: errorMessage,
        errorMessage,
        isGenericError: isError || (dataRegistration && !isSent && !isSuccessRegistration) || false,
        isBusinessError: isSuccess && !isSuccessRegistration && !dataRegistration,
      });

      navigate('/perfil/editar/endereco/erro', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isSuccessRegistration, isSuccess, isSent]);

  const handleEventCallback = (status: 'erro' | 'sucesso') => {
    const errorMessage =
      errorId ??
      userMessage?.errorTitle ??
      error?.message ??
      dataRegistration?.message ??
      (error as any)?.data?.errorId;

    const analyticsData: AnalyticsType = {
      event: 'analytics-event',
      event_parameters: {
        event_name: `callback_alteracao_endereco_${status}`,
        ...(status === 'erro' && {
          cd_error_message: slugify(errorMessage ?? '', {
            lower: true,
            replacement: '-',
          }),
        }),
      },
    };

    sendDataToGTM(analyticsData);
  };

  const handleOnClickCorrectAddress = () => {
    handleCorrectData();
    navigate('/perfil/editar/endereco/confirmacao', { replace: true });
  };

  const AddressComponent = () => {
    if (!steps.userData) return null;

    const numberFormatted = steps.userData?.hasNoNumber ? 'S/N' : steps.userData?.number;

    return (
      <Flex direction='rows' gap='$2'>
        <ResponsiveFloraTypography fontSize='bodySmallShort' color='$nonInteractivePredominant'>
          {steps.userData?.address}, {numberFormatted}
          <br />
          {steps.userData?.district}, {steps.userData?.city}, {steps.userData?.state}
          <br />
          CEP {steps.userData?.zipcode}
          <br />
          {steps.userData?.complement}
          <br />
        </ResponsiveFloraTypography>
      </Flex>
    );
  };

  if (!steps.start || !steps.validate || !steps.confirmation || !steps.userData) {
    return <Navigate to='/perfil' replace />;
  }

  return (
    <StylesDrawers.DrawerSection direction='column'>
      <Flex direction='column' gap='$4' data-autotagging-flora-section='alterar_endereco'>
        <StylesDrawers.FullFlex gap='$2' direction='column' align='center'>
          <InformationCircleIcon color='$backgroundAltSecondary' size={50} aria-hidden />
          <ResponsiveFloraTypography
            color='$backgroundAltPrimary'
            fontSize='title'
            fontWeight='bold'
          >
            Confirmação de endereço
          </ResponsiveFloraTypography>
        </StylesDrawers.FullFlex>
        <Flex direction='column'>
          <Styles.Grid>
            <Styles.Card>
              <Flex css={{ gridArea: 'content' }} direction='column' gap='$4'>
                <Flex direction='rows' gap='$2' align='center'>
                  <TruckIcon color='$backgroundAltPrimary' size={24} />
                  <ResponsiveFloraTypography
                    fontWeight='medium'
                    fontSize='bodySmallShort'
                    color='$backgroundAltPrimary'
                  >
                    Seu novo endereço:
                  </ResponsiveFloraTypography>
                </Flex>
                <AddressComponent />
              </Flex>
            </Styles.Card>
          </Styles.Grid>
        </Flex>
        <Flex direction='column'>
          <Styles.CardAlertStandard>
            <Styles.Grid>
              <Flex
                css={{ gridArea: 'content' }}
                direction='column'
                gap='$4'
                justify='center'
                align='center'
              >
                <Flex gap='$4' align='center'>
                  <ExclamationMarkTriangleFillIcon color='$alert-standard' size={24} aria-hidden />
                  <ResponsiveFloraTypography
                    fontSize='exceptionsAuxiliar'
                    color='$backgroundAltSecondary'
                  >
                    O seu {config.registration.infoResellerPlace} poderão mudar, de acordo com o
                    novo endereço.
                  </ResponsiveFloraTypography>
                </Flex>
              </Flex>
            </Styles.Grid>
          </Styles.CardAlertStandard>
        </Flex>

        <Flex direction='column' gap='$2'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FloraButton
              disabled={isLoading}
              autotagging-flora='on'
              data-flora='button'
              data-flora-text='confirmar-alteracao-endereco'
              type='submit'
              isFull
            >
              <ResponsiveFloraTypography fontWeight='bold'>
                Confirmar alteração de endereço
              </ResponsiveFloraTypography>
            </FloraButton>
          </form>
        </Flex>
        <Flex direction='column'>
          <FloraButton
            disabled={isLoading}
            autotagging-flora='on'
            data-flora='button'
            data-flora-text='corrigir-endereco'
            hierarchy='secondary'
            onClick={handleOnClickCorrectAddress}
          >
            <ResponsiveFloraTypography fontWeight='bold'>
              Corrigir endereço
            </ResponsiveFloraTypography>
          </FloraButton>
        </Flex>
      </Flex>
    </StylesDrawers.DrawerSection>
  );
};
